import React from 'react';
import { Card, Badge, theme } from 'antd';
import { InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { primaryShades, statusTokens } from '../../../theme/theme';
import CustomTooltip from '../Modals/CustomTooltip';

const { useToken } = theme;

// Costanti dei colori
const PRIMARY_500 = primaryShades.primary500;
const ERROR = statusTokens.error;
const BORDER_COLOR = '#e8e8e8';
const DEFAULT_TEXT_COLOR = '#333';
const DEFAULT_BG_COLOR = '#f9f9f9';

/**
 * Componente che visualizza un elemento di monitoraggio delle risorse
 * @param {string} count - Numero da visualizzare (se null/undefined, mostra come pulsante di navigazione)
 * @param {string} label - Etichetta principale
 * @param {string} sublabel - Etichetta secondaria (opzionale)
 * @param {string} color - Colore del testo e dei numeri
 * @param {string} bgColor - Colore di sfondo
 * @param {number} badgeCount - Numero da visualizzare nel badge (opzionale)
 * @param {boolean} isLast - Indica se è l'ultimo elemento nella lista
 * @param {boolean} isFirst - Indica se è il primo elemento nella lista
 * @param {string} customText - Testo personalizzato per il pulsante di navigazione
 * @param {string} tooltip - Testo del tooltip personalizzato
 * @param {React.ReactNode} customIcon - Icona personalizzata per il pulsante di navigazione
 */
const AssetMonitorItem = ({
    count,
    label,
    sublabel,
    color = DEFAULT_TEXT_COLOR,
    bgColor = DEFAULT_BG_COLOR,
    badgeCount,
    isLast,
    isFirst,
    customText,
    tooltip,
    customIcon
}) => {
    const { token } = useToken();

    // Se count è null/undefined, mostra come pulsante di navigazione
    const isNavigationButton = count === null || count === undefined;

    return (
        <Card
            style={{
                borderRadius: '0',
                height: 'clamp(9rem, 20vh, 11.875rem)',
                boxShadow: 'none',
                border: `clamp(0.0625rem, 0.2vw, 0.0625rem) solid ${BORDER_COLOR}`,
                borderRight: isLast ? `clamp(0.0625rem, 0.2vw, 0.0625rem) solid ${BORDER_COLOR}` : 'none',
                marginRight: 'clamp(-0.0625rem, -0.2vw, -0.0625rem)',
                borderTopLeftRadius: isFirst ? 'clamp(0.25rem, 2vw, 0.5rem)' : '0',
                borderBottomLeftRadius: isFirst ? 'clamp(0.25rem, 2vw, 0.5rem)' : '0',
                borderTopRightRadius: isLast ? 'clamp(0.25rem, 2vw, 0.5rem)' : '0',
                borderBottomRightRadius: isLast ? 'clamp(0.25rem, 2vw, 0.5rem)' : '0',
                backgroundColor: isNavigationButton ? '#f8f9fa' : bgColor,
                cursor: isNavigationButton ? 'pointer' : 'default'
            }}
            styles={{ body: { padding: 'clamp(0.75rem, 3vw, 1.25rem)', height: '100%' } }}
        >
            {isNavigationButton ? (
                // Modalità pulsante di navigazione
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%'
                }}>
                    {customIcon || <PlusOutlined style={{
                        fontSize: 'clamp(1.5rem, 5vw, 2rem)',
                        color: token.colorPrimary,
                        marginBottom: 'clamp(0.375rem, 2vw, 0.625rem)'
                    }} />}
                    <div style={{
                        color: token.colorPrimary,
                        fontSize: 'clamp(0.6875rem, 1.5vw, 0.75rem)',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        lineHeight: '1.3',
                        marginTop: customIcon ? 'clamp(0.375rem, 2vw, 0.625rem)' : 0
                    }}>
                        {customText || "GO TO LIBRARY"}
                    </div>
                </div>
            ) : (
                // Modalità normale con dati
                <div style={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%'
                }}>
                    <CustomTooltip title={tooltip || "Asset information"}>
                        <InfoCircleOutlined
                            style={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                fontSize: 'clamp(0.75rem, 2vw, 1rem)',
                                color: PRIMARY_500
                            }}
                        />
                    </CustomTooltip>
                    <div style={{ position: 'relative', width: '100%', textAlign: 'center' }}>
                        <span style={{
                            fontSize: typeof count === 'string' && count.length > 10
                                ? 'clamp(1rem, 3vw, 1.5rem)'
                                : 'clamp(2rem, 6vw, 3rem)',
                            fontWeight: 'bold',
                            color: color,
                            marginBottom: 'clamp(0.25rem, 1vw, 0.3125rem)',
                            display: 'block',
                            lineHeight: typeof count === 'string' && count.length > 10 ? 1.2 : 1,
                            wordBreak: 'break-word',
                            hyphens: 'auto',
                            padding: '0 0.25rem'
                        }}>
                            {count}
                            {badgeCount && (
                                <Badge
                                    count={badgeCount}
                                    style={{
                                        backgroundColor: ERROR,
                                        position: 'absolute',
                                        top: 'clamp(-2rem, -7vw, -2.5rem)',
                                        right: 'clamp(-0.75rem, -3vw, -0.9375rem)'
                                    }}
                                />
                            )}
                        </span>
                    </div>
                    <div style={{
                        marginTop: 'clamp(0.625rem, 3vw, 0.9375rem)',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            fontSize: 'clamp(0.6875rem, 1.5vw, 0.75rem)',
                            color: color,
                            fontWeight: 'bold',
                            lineHeight: '1.3'
                        }}>
                            {label}
                        </div>
                        {sublabel && (
                            <div style={{
                                fontSize: 'clamp(0.6875rem, 1.5vw, 0.75rem)',
                                color: color,
                                fontWeight: 'bold',
                                lineHeight: '1.3'
                            }}>
                                {sublabel}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Card>
    );
};

export default AssetMonitorItem; 